import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { motion } from "framer-motion";
import HeatMap from "@uiw/react-heat-map";
import { useCourse } from "../../../../context/CourseContext";
import {
  Calendar,
  CalendarDays,
  HelpCircle,
} from "lucide-react";
import {
  card,
  microLabel,
  bodySecondary,
  accentBarSection,
  accentBarCard,
  surfaceMuted,
  badgeBase,
  motionFadeUp,
  motionStaggerChild,
  pageTitle,
  emptyState,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";

const AttendanceHeatMap = () => {
  const { courseData, savedSessions } = useCourse();
  const [attendanceData, setAttendanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [heatMapLayout, setHeatMapLayout] = useState({ rectSize: 24, space: 8, height: 210 });

  // Tooltip state (portal-based)
  const [tooltip, setTooltip] = useState(null);
  const tooltipTimeout = useRef(null);

  // Track dark mode reliably (Tailwind uses "dark" class on <html>)
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setIsDarkMode(el.classList.contains("dark"));
    update();

    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  // Responsive sizing for the heatmap grid
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const apply = () => {
      if (mq.matches) {
        setHeatMapLayout({ rectSize: 16, space: 4, height: 180 });
      } else {
        setHeatMapLayout({ rectSize: 24, space: 8, height: 210 });
      }
    };
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  // Convert savedSessions Set to a stable array for dependency comparison
  const savedSessionsArray = useMemo(() => {
    return Array.from(savedSessions || []);
  }, [savedSessions]);

  // Derive year from semester start date, fallback to current year
  const heatmapYear = useMemo(() => {
    if (courseData?.courseSchedule?.classStartDate) {
      return new Date(courseData.courseSchedule.classStartDate).getFullYear();
    }
    return new Date().getFullYear();
  }, [courseData?.courseSchedule?.classStartDate]);

  // Custom function to determine color based on percentage
  const getColorForPercentage = (percentage) => {
    if (percentage === 0) return "#ef4444";
    if (percentage < 26) return "#ef4444";
    if (percentage < 51) return "#facc15";
    if (percentage < 76) return "#84cc16";
    return "#1aa100";
  };

  const formatHeatMapDateForTooltip = (dateStr) => {
    const [y, m, d] = (dateStr || "").split("/").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return "Unknown date";
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Compute attendance data — no longer gated on semester dates
  const processedAttendanceData = useMemo(() => {
    if (
      !courseData ||
      !courseData.attendance ||
      !courseData.attendance.sessions ||
      !courseData.students
    ) {
      return [];
    }

    const heatMapData = [];
    const allSessions = Array.isArray(courseData.attendance.sessions) ? courseData.attendance.sessions : [];
    const totalStudents = courseData.students.length;

    const filteredSessions = allSessions.filter((session) => {
      if (savedSessionsArray.length === 0) return true;
      const dateStr = (session.date || "").includes("T") ? session.date.split("T")[0] : session.date;
      const sessionKey = `${dateStr}_${session.time || ""}_${session.branch || ""}`;
      return savedSessionsArray.includes(sessionKey);
    });

    const sessionsByDate = {};

    filteredSessions.forEach((session) => {
      const dateStr = (session.date || "").includes("T") ? session.date.split("T")[0] : session.date;

      if (!sessionsByDate[dateStr]) {
        sessionsByDate[dateStr] = {
          totalSessions: 0,
          totalAttendance: 0,
        };
      }

      sessionsByDate[dateStr].totalSessions += 1;

      const presentCount = Array.isArray(session.attendees) ? session.attendees.length : 0;
      sessionsByDate[dateStr].totalAttendance += presentCount;
    });

    Object.entries(sessionsByDate).forEach(([dateStr, data]) => {
      const [year, month, day] = dateStr.split("-");
      const formattedDate = `${year}/${month}/${day}`;

      const maxPossibleAttendance = totalStudents * data.totalSessions;

      const percentage =
        maxPossibleAttendance > 0
          ? Math.round((data.totalAttendance / maxPossibleAttendance) * 100)
          : 0;

      const color = getColorForPercentage(percentage);

      heatMapData.push({
        date: formattedDate,
        count: percentage,
        color: color,
        raw: {
          attended: data.totalAttendance,
          possible: maxPossibleAttendance,
          sessions: data.totalSessions,
        },
      });
    });

    return heatMapData;
  }, [courseData?.attendance?.sessions, courseData?.students, savedSessionsArray]);

  // Update state only when processed data changes
  useEffect(() => {
    setIsLoading(false);
    setAttendanceData(processedAttendanceData);
  }, [processedAttendanceData]);

  // Calculate overall attendance percentage
  const calculateOverallAttendance = () => {
    if (attendanceData.length === 0) return 0;

    const totalAttended = attendanceData.reduce(
      (sum, day) => sum + (day.raw?.attended || 0),
      0
    );
    const totalPossible = attendanceData.reduce(
      (sum, day) => sum + (day.raw?.possible || 0),
      0
    );

    return totalPossible > 0
      ? Math.round((totalAttended / totalPossible) * 100)
      : 0;
  };

  // Portal tooltip handlers
  const handleRectMouseEnter = useCallback((data, e) => {
    if (tooltipTimeout.current) clearTimeout(tooltipTimeout.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      x: rect.left + rect.width / 2,
      y: rect.top,
      date: data.date,
      count: data.count,
      raw: data.raw,
    });
  }, []);

  const handleRectMouseLeave = useCallback(() => {
    tooltipTimeout.current = setTimeout(() => setTooltip(null), 100);
  }, []);

  // Stat cards config
  const overallPct = calculateOverallAttendance();
  const totalSessions = attendanceData.reduce((sum, day) => sum + (day.raw?.sessions || 0), 0);
  const daysRecorded = attendanceData.length;
  const studentCount = courseData?.students?.length || 0;

  const statCards = [
    {
      label: "Overall Attendance",
      value: `${overallPct}%`,
      badge: overallPct >= 75 ? "Good" : "Poor",
      badgeColor: overallPct >= 75
        ? "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700"
        : "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-900/30 dark:border-red-700",
      sub: "average",
    },
    { label: "Total Sessions", value: totalSessions },
    { label: "Days Recorded", value: daysRecorded },
    { label: "Students", value: studentCount },
  ];

  // Render content based on state
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={cn(card, "p-6")}>
              <div className="animate-pulse space-y-3">
                <div className={accentBarCard} style={{ opacity: 0.3 }} />
                <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded-sm" />
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              </div>
            </div>
          ))}
          <div className={cn(card, "p-6 md:col-span-4")}>
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-sm" />
              <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-sm" />
            </div>
          </div>
        </div>
      );
    }

    if (attendanceData.length === 0) {
      return (
        <motion.div {...motionFadeUp} className={cn(emptyState, "py-16 px-4")}>
          <CalendarDays className="h-10 w-10 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-black tracking-tight text-gray-800 dark:text-white mb-2">
            No Attendance Data
          </h3>
          <p className={bodySecondary}>
            Start recording attendance to generate your heat map. Each day will
            show the attendance percentage.
          </p>
        </motion.div>
      );
    }

    return (
      <>
        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {statCards.map((s, i) => (
            <motion.div
              key={s.label}
              {...motionStaggerChild(i)}
              className={cn(card, "p-6")}
            >
              <div className={accentBarCard} />
              <div className="flex items-center justify-between mt-3">
                <span className={microLabel}>{s.label}</span>
                {s.badge && (
                  <span className={cn(badgeBase, s.badgeColor)}>
                    {s.badge}
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-end">
                <span className="text-3xl font-black tracking-tight text-accent1">
                  {s.value}
                </span>
                {s.sub && (
                  <span className={cn(bodySecondary, "ml-2 mb-1")}>{s.sub}</span>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Legend */}
        <motion.div
          {...motionStaggerChild(4)}
          className={cn(surfaceMuted, "mb-6 flex flex-wrap justify-between gap-4 p-5 rounded-sm border border-gray-200 dark:border-gray-700")}
        >
          {[
            { color: "#ef4444", label: "Low (1-25%)" },
            { color: "#facc15", label: "Medium (26-50%)" },
            { color: "#84cc16", label: "High (51-75%)" },
            { color: "#1aa100", label: "Full (76-100%)" },
          ].map((item) => (
            <div key={item.label} className="flex items-center">
              <span className="block w-4 h-4 rounded-sm mr-2" style={{ backgroundColor: item.color }} />
              <span className={bodySecondary}>{item.label}</span>
            </div>
          ))}
        </motion.div>

        {/* Heatmap container */}
        <motion.div
          {...motionStaggerChild(5)}
          className={cn(card, "relative p-4 sm:p-6 overflow-x-auto")}
        >
          <div className="min-w-[620px] sm:min-w-[750px]">
            <HeatMap
              value={attendanceData}
              width={"100%"}
              height={heatMapLayout.height}
              rectSize={heatMapLayout.rectSize}
              space={heatMapLayout.space}
              rectProps={{
                rx: 2,
              }}
              startDate={new Date(heatmapYear, 0, 1)}
              endDate={new Date(heatmapYear, 11, 31)}
              legendCellSize={0}
              max={100}
              panelColors={{
                0: isDarkMode ? "#374151" : "#f3f4f6",
                100: "#1aa100",
              }}
              rectRender={(props, data) => {
                if (data.count === undefined) {
                  return <rect {...props} />;
                }

                const fillColor =
                  data.color || getColorForPercentage(data.count);
                const newProps = { ...props, fill: fillColor };

                return (
                  <rect
                    {...newProps}
                    onMouseEnter={(e) => handleRectMouseEnter(data, e)}
                    onMouseLeave={handleRectMouseLeave}
                  />
                );
              }}
            />
          </div>
        </motion.div>

        {/* Info box */}
        <motion.div
          {...motionStaggerChild(6)}
          className="flex items-start p-5 rounded-sm bg-accent1/5 border border-accent1/20 mt-6"
        >
          <HelpCircle className="w-5 h-5 text-accent1 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-accent1 font-bold text-sm mb-1">
              How to read this chart
            </p>
            <p className={bodySecondary}>
              Each square represents a day of attendance. Darker green indicates
              higher attendance rate. Hover over a square to see detailed
              information about that day's attendance.
            </p>
          </div>
        </motion.div>
      </>
    );
  };

  return (
    <motion.div {...motionFadeUp} className="p-4 sm:p-6 lg:p-8 space-y-8 gg-attendance-heatmap">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className={pageTitle}>
            Attendance Heat Map
          </h1>
          <p className={cn(bodySecondary, "mt-1")}>
            Visual representation of attendance patterns over time
          </p>
          <div className={cn(accentBarSection, "mt-3")} />
        </div>
      </div>

      {/* Ensure HeatMap SVG labels (months/weekdays) are readable in dark mode */}
      <style>{`
        .gg-attendance-heatmap .w-heatmap text,
        .gg-attendance-heatmap svg.w-heatmap text {
          fill: #6b7280 !important;
        }
        .dark .gg-attendance-heatmap .w-heatmap text,
        .dark .gg-attendance-heatmap svg.w-heatmap text,
        .dark .gg-attendance-heatmap .w-heatmap svg text {
          fill: #ffffff !important;
        }
      `}</style>

      {renderContent()}

      {/* Portal tooltip */}
      {tooltip && ReactDOM.createPortal(
        <div
          className="fixed z-[9999] pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: "translate(-50%, -100%)",
          }}
        >
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-[11px] leading-tight px-2.5 py-1.5 rounded-sm shadow-lg whitespace-nowrap">
            <div className="font-bold">{formatHeatMapDateForTooltip(tooltip.date)}</div>
            <div className="text-gray-300 dark:text-gray-400 mt-0.5">Attendance: {tooltip.count}%</div>
            <div className="text-gray-300 dark:text-gray-400">
              Present: {tooltip.raw?.attended || 0}/{tooltip.raw?.possible || 0}
            </div>
            <div className="text-gray-300 dark:text-gray-400">
              Sessions: {tooltip.raw?.sessions || 0}
            </div>
          </div>
          <div
            className="mx-auto w-0 h-0"
            style={{
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: isDarkMode ? "5px solid rgb(55 65 81)" : "5px solid rgb(17 24 39)",
            }}
          />
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default AttendanceHeatMap;
